<template>
  <div class="date-picker-container">
    <v-date-picker
      :id="id"
      v-model="content"
      :masks="{ L: 'MMMM D, YYYY' }"
      :min-date="minDate"
      :max-date="maxDate"
      :select-attribute="selectedDate"
      :popover="{ visibility: 'focus' }"
    >
      <template #default="{ inputValue, inputEvents, hidePopover }">
        <span>
          <input
            class="border-1 focus:outline-none focus:border-2 focus:border-grey-clickable rounded-xl min-w-320 text-grey-body"
            :class="[datePickerInput, datePickerValid, customClasses]"
            :placeholder="labelName"
            :value="inputValue"
            v-on="inputEvents || {}"
            @keypress.enter.prevent="emitManualInput(inputValue, $event, hidePopover)"
            @blur="moveLabelDown"
            @focus="moveLabelUp"
          />
          <label :for="id" :class="datePickerLabel">{{ labelName }}</label>
          <div v-if="hasIcon" class="text-field-icon">
            <calendar-outline-icon color="#4B4C52" />
          </div>
        </span>
      </template>
    </v-date-picker>
    <HelperText :id="id" :text="helperText" :status="status" />
  </div>
</template>

<script lang="ts">
import { DatePicker } from 'v-calendar';
import helperStatus from '../../helpers/status';
import HelperText from '../helper_text/HelperText.vue';
import CalendarOutlineIcon from '../../graphics/icons/CalendarOutlineIcon.vue';
/**
 * DatePicker is used to allow the user to pick a particular date and time services are needed.
 * This version uses v-calendar (https://vcalendar.io/examples/datepickers.html). Previously, we used Bootstrap calendar, but had to transition away from it in order to upgrade to Vue 3.
 * A user must be able to navigate this component using a keyboard. Calendar must appear on focus, not on hover.
 */
export default {
  name: 'PoppinsDatePicker',
  components: {
    VDatePicker: DatePicker,
    HelperText,
    CalendarOutlineIcon,
  },
  props: {
    /**
     * Label is required for screenreader.
     */
    labelName: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    /**
     * Id is required for label.
     */
    id: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    /**
     * We always want to disable dates in the past, but we may also want to disable today, i.e. not allow same-day requests.
     */
    minDate: {
      type: Date,
      default: () => new Date(),
      required: false,
    },
    /**
     * Disable future dates if necessary. Max-date can vary, or we could have no limit.
     */
    maxDate: {
      type: Date,
      default: null,
      required: false,
    },
    /**
     * Value is the date that comes from the parent component. It gets copied to local variable called `content`.
     */
    value: {
      type: Date,
      default: null,
      required: false,
    },
    /**
     * Prop that reflects whether input passed validation.
     */
    status: {
      type: Number as () => helperStatus,
      default: helperStatus.Neutral,
    },
    /**
     * Prop that shows error text. Works together with `status`.
     */
    helperText: {
      type: String,
      default: '',
      required: false,
    },
    /**
     * Prop that passes custom classes from parent component to date picker.
     */
    customClasses: {
      type: String,
      default: '',
      required: false,
    },
    hasIcon: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data(): any {
    return {
      /**
       * Local copy of value from parent.
       */
      content: this.value,
      datePickerLabel: '',
      datePickerInput: '',
      /**
       * V-calendar's way of styling the calendar popover.
       */
      selectedDate: {
        highlight: {
          style: {
            backgroundColor: '#99005c',
          },
          contentStyle: {
            color: 'white',
          },
        },
      },
    };
  },
  computed: {
    datePickerValid(): String {
      return this.hasError ? 'border-critical' : 'border-grey-graphics-subdued';
    },
    hasError(): Boolean {
      return this.status === helperStatus.Error;
    },
    maxStartDate(): any {
      const today = new Date();
      const thirtyDaysFromToday = new Date();
      // returning the code below will return a number, don't do it, you need to return a date
      thirtyDaysFromToday.setDate(today.getDate() + 30);

      // returning the variable like this will return a date
      return thirtyDaysFromToday;
    },
  },
  watch: {
    content(selectedDate: String) {
      this.$emit('input', selectedDate);
    },
    value(newDate: String) {
      this.content = newDate;
    },
  },
  created() {
    if (this.value) {
      this.moveLabelUp();
    } else {
      this.moveLabelDown();
    }
  },
  methods: {
    // This method enables us to validate input when user types in the date instead of using the calendar popover.
    // Parent is listening to @manual-input and runs whatever validation needs to happen when it fires.
    // Parent will receive the date as a string and will need to handle type coercion.
    emitManualInput(inputValue, event, hidePopover) {
      this.$emit('manual-input', inputValue);
      event.target.blur();
      hidePopover();
    },
    // The label "moves" up and down visually. It doesn't actually move in the DOM.
    // We are making it appear this way to the user by making the label transparent when the placeholder is in place,
    // and making placeholder transparent when the label is in place
    moveLabelUp() {
      this.datePickerLabel = 'date-picker-label-filled';
      this.datePickerInput = 'date-picker-input-filled';
    },
    moveLabelDown() {
      if (!this.content) {
        this.datePickerLabel = 'date-picker-label-empty';
        this.datePickerInput = 'date-picker-input-empty';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.date-picker-container {
  position: relative;
}

input::placeholder {
  color: theme('colors.grey.text.subdued');
}

/* move label up */
.date-picker-label-filled {
  top: 0.3rem;
  left: 1rem;
  color: #6e6f74;
  letter-spacing: 0.09px;
  position: absolute;
  font-size: 12px;
  transition:
    top 150ms cubic-bezier(0.4, 0, 0.2, 1),
    font-size 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

input:focus::placeholder {
  color: transparent;
}

.date-picker-input-filled {
  padding: 1.5rem 1rem 0.5rem;
}

/* move label down */
.date-picker-input-empty {
  padding: 1rem;
}

.date-picker-label-empty {
  display: none;
}

.text-field-icon {
  position: absolute;
  left: calc(100% - 2rem);
  top: 18px;
  z-index: 2;
  &::after {
    content: '';
    display: block;
    background-color: theme('colors.foreground');
    height: 28px;
    width: 28px;
    position: absolute;
    top: 1px;
    z-index: -1;
    right: 8px;
  }
}
</style>
